/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';

// add this interface for future customization
export interface ILine {
	data: string;
	type?: string;
}

export enum LineDisplayType {
	Text = 'text',
	Html = 'html',
}

@Component({
	selector: 'text-display-container',
	templateUrl: './text-display-container.component.html',
	styleUrls: ['./text-display-container.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * This component is a console-like text display, with virtual viewport scrolling
 * this.data would be the single source of truth
 *
 * Note: this component could maintain a mutable reference to source array via @Input()
 * Usage:
 * @Input() - data for initial data
 * public appendLine() for adding subsequent data (i.e. async data)
 */
export class TextDisplayContainerComponent {
	@ViewChild('virtualScrollViewport', { static: false })
	public virtualScrollViewport: CdkVirtualScrollViewport;

	@Input() public data: ILine[] = [];

	public lineDisplayType = LineDisplayType;

	/*
	 * dev note:
	 * Since data to be display could have various length, and this causes different row height
	 * We can't specify itemSize which result erratic scrolling behavior
	 * Set itemSize to 0 for now, which results all items to be rendered
	 *
	 * Solution:
	 * 1) We can implement our own scrolling strategy by extending VirtualScrollStrategy
	 * 2) Material has an autosize that works for scrolling over various size items, in @angular/cdk-experimental, not production ready yet... so wait?
	 */

	constructor(private changeDetectorRef: ChangeDetectorRef) {}

	public appendLine(newLines: ILine | ILine[]): void {
		if (!Array.isArray(newLines)) {
			newLines = [newLines];
		}

		this.data = this.data.concat(newLines);
		this.virtualScrollViewport.scrollTo({ bottom: 0, behavior: 'auto' });
		this.changeDetectorRef.detectChanges();
	}

	public getText(): string[] {
		return this.data.map((line) => line.data);
	}
}
